import { useState } from 'react';
import { useData } from '../data.js';
import { setCredential, type ProviderCredential } from '../api.js';
import { SendingPanel } from './Panels.js';
import { MailboxPanel } from './MailboxPanel.js';
import { Offerings } from './Offerings.js';
import { Profile } from './Profile.js';
import { Team, YourAccount } from './Team.js';
import { useEffect, useState as useLocalState } from 'react';
import { whoAmI } from '../api.js';
import type { Identity } from '../api.js';

// Each entry says where to get the key AND what the app does without it.
// "Without it" is the more useful half: none of these is required to start, and
// an operator who thinks they all are will go and get five accounts before
// opening the CRM once.
const PROVIDER_HELP: Record<string,
  { label: string; where: string; note: string; without: string }> = {
  apollo: {
    label: 'Apollo',
    where: 'Apollo → Settings → Integrations → API → Create new key',
    note: 'Account enrichment and contact sourcing. Search is free; enrichment costs credits.',
    without: 'Add accounts and contacts by hand. Nothing else changes.',
  },
  anthropic: {
    label: 'Anthropic',
    where: 'console.anthropic.com → API Keys → Create Key '
      + '(the account needs credits — a key alone is not enough)',
    note: 'Six agents run on this: account chat, the cadence recommender, capture, '
      + 'the domain finder, the ICP drafter, and the research agent.',
    without: 'Setup still works — it reads your description with pattern matching '
      + 'instead and says so. ICP scoring is arithmetic and never needs a key. The six '
      + 'agents above refuse and tell you why rather than failing quietly.',
  },
  slack: {
    label: 'Slack',
    where: 'api.slack.com/apps → your app → OAuth & Permissions → Bot User OAuth Token',
    note: 'Second source for the deal sweeper. Needs channels:history and '
      + 'channels:read to scan for opportunities.',
    without: 'The sweeper reads email only.',
  },
  microsoft: {
    label: 'Microsoft Graph',
    where: 'portal.azure.com → App registrations → your app → Certificates & secrets',
    note: 'Reads replies from your real mailbox. Needs the Mail.Read permission '
      + 'with admin consent granted.',
    without: 'Nothing sees inbound mail, so auto-pause on reply never fires. '
      + 'Log replies by hand or a sequence will keep writing to someone who answered.',
  },
  resend: {
    label: 'Resend',
    where: 'resend.com → API Keys. Then Domains → add yours → add the DNS records '
      + 'it gives you → wait for Verified, and mark it verified below in Sending.',
    note: 'Outbound email. This key plus a verified domain is the whole gap '
      + 'between a queued message and a sent one.',
    without: 'Everything up to the send works — compose, sequence, schedule. '
      + 'The engine refuses at the last step and says which piece is missing.',
  },
  asana: {
    label: 'Asana',
    where: 'Asana → My Settings → Apps → Personal access tokens',
    note: 'Two-way sync with the pipeline board.',
    without: 'The CRM is the only record. Nothing is lost.',
  },
};

function CredentialRow({ p, onSaved }: { p: ProviderCredential; onSaved: () => void }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const help = PROVIDER_HELP[p.provider]
    ?? { label: p.provider, where: '', note: '', without: '' };

  async function save(next: string) {
    setBusy(true); setError(null); setMsg(null);
    try {
      const r = await setCredential(p.provider, next);
      setValue('');
      setMsg(r.configured ? `Saved, ending ${r.masked?.slice(-4)}.` : 'Removed.');
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cred">
      <div className="row">
        <strong>{help.label}</strong>
        {p.source === 'env' && <span className="flag flag-mute">from .env</span>}
        {p.source === 'database' && <span className="flag flag-ok">configured</span>}
        {!p.configured && <span className="flag flag-warn">not set</span>}
      </div>
      <p className="meta">{help.note}</p>
      {!p.configured && help.without && (
        <p className="meta"><em>Without it:</em> {help.without}</p>
      )}

      {p.editable ? (
        <>
          <div className="actions">
            <input
              type="password"
              autoComplete="off"
              placeholder={p.configured ? 'Paste a new key to replace' : 'Paste key'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && value.trim()) void save(value.trim());
              }}
            />
            <button disabled={busy || !value.trim()} onClick={() => void save(value.trim())}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            {p.configured && (
              <button className="mini" disabled={busy} onClick={() => void save('')}>
                Remove
              </button>
            )}
          </div>
          <p className="meta">
            Get it from {help.where}. Encrypted before it's stored — it can't be read back,
            only replaced.
          </p>
        </>
      ) : (
        <p className="meta">
          Set in the environment, which takes precedence. Remove it from <code>.env</code> to
          manage this key here instead.
        </p>
      )}

      {msg && <p className="ok-note">{msg}</p>}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

const STATUS_CLASS: Record<string, string> = {
  connected: 'flag-ok',
  verified: 'flag-ok',
  degraded: 'flag-warn',
  pending: 'flag-warn',
  unverified: 'flag-warn',
  error: 'flag-block',
  failed: 'flag-block',
  revoked: 'flag-block',
  not_configured: 'flag-mute',
};

export function SettingsView() {
  const { settings: s, retry } = useData();
  const [me, setMe] = useLocalState<Identity | null>(null);

  useEffect(() => {
    // 401s for a workspace-key session, which is not an error — there is
    // simply no person to show an account panel for.
    whoAmI().then(setMe).catch(() => setMe(null));
  }, []);

  if (!s) return <p className="loading">Loading…</p>;

  const unhealthy = s.connections.filter((c) => c.status !== 'connected');

  return (
    <div className="settings">
      {/* Ordered by what depends on what. Keys are useless without a profile
          to draft from, so the profile is not buried under the plumbing. */}
      <Profile />
      <Offerings />

      <Team />
      {me && <YourAccount email={me.user.email} name={me.user.name} />}

      <section className="panel">
        <h4>API keys</h4>
        <p className="meta">
          Stored per workspace and encrypted at rest, so each org brings its own keys —
          a shared <code>.env</code> can't scale to multiple tenants.
        </p>
        {(s.providers ?? []).map((p) => (
          <CredentialRow key={p.provider} p={p} onSaved={retry} />
        ))}
        {!s.providers && (
          <p className="empty">Restart the dev server to pick up the credentials API.</p>
        )}
      </section>

      <MailboxPanel />
      <SendingPanel />

      <section className="panel">
        <h4>Organization</h4>
        <p className="meta">
          {s.org.name} · {s.counts.companies} accounts · {s.counts.contacts} contacts ·{' '}
          {s.counts.sequences} sequences
        </p>
        {s.members.map((m) => (
          <div className="person" key={m.email}>
            <div>
              <strong>{m.name ?? m.email}</strong>
              <span className="meta">{m.email}</span>
            </div>
            <span className="flag flag-mute">{m.role}</span>
          </div>
        ))}
        <p className="meta">
          Multi-tenant schema — every record carries an org id, so a second organization is a
          row, not a migration.
        </p>
      </section>

      <section className="panel">
        <h4>
          Connections
          {unhealthy.length > 0 && (
            <span className="flag flag-warn" style={{ marginLeft: 8 }}>
              {unhealthy.length} need attention
            </span>
          )}
        </h4>
        <table className="conns">
          <tbody>
            {s.connections.map((c) => (
              <tr key={c.id}>
                <td className="prov">{c.provider}</td>
                <td>
                  <span className={`flag ${STATUS_CLASS[c.status] ?? 'flag-mute'}`}>
                    {c.status.replace('_', ' ')}
                  </span>
                </td>
                <td className="note">
                  {c.meta?.note ?? c.last_error ?? c.secret_ref ?? ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="meta">
          Health only. Keys are stored per workspace and encrypted (see API keys above);
          this shows whether each connection is working, never the secret itself.
        </p>
      </section>

      <section className="panel">
        <h4>Sending identity</h4>
        {s.identities.map((i) => (
          <div className="identity" key={i.id}>
            <div className="row">
              <strong>{i.from_email}</strong>
              <span className={`flag ${STATUS_CLASS[i.status] ?? 'flag-mute'}`}>{i.status}</span>
            </div>
            <div className="dns">
              {(['spf', 'dkim', 'dmarc'] as const).map((k) => {
                const v = i[`${k}_ok` as const];
                return (
                  <span
                    key={k}
                    className={`flag ${v === true ? 'flag-ok' : v === false ? 'flag-block' : 'flag-mute'}`}
                  >
                    {k.toUpperCase()} {v === true ? 'ok' : v === false ? 'fail' : '—'}
                  </span>
                );
              })}
            </div>
            <p className="meta">
              App runs on app.xsellr8now.com · mail leaves as {i.domain} · cap{' '}
              {i.daily_send_cap}/day. Where the app is hosted and what it sends as are separate
              things.
            </p>
          </div>
        ))}
      </section>
    </div>
  );
}
